


import { useState, useEffect, useRef } from "react";
import axiosInstance from "../api/axiosInstance";
import { supplierOptions } from "../data/formDefaults";
import { useNavigate } from "react-router-dom";

// ── REUSABLE MULTI-SELECT CHECKBOX DROPDOWN ────────────
const MultiSelect = ({ label, options, selected, onChange, placeholder = "All" }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (val) => {
    if (selected.includes(val)) {
      onChange(selected.filter((v) => v !== val));
    } else {
      onChange([...selected, val]);
    }
  };

  const triggerLabel =
    selected.length === 0
      ? <span className="text-slate-400">{placeholder}</span>
      : selected.length === 1
        ? <span className="text-slate-800">{selected[0]}</span>
        : <span className="text-slate-800">{selected.length} selected</span>;

  return (
    <div ref={ref} className="relative">
      <label className="block text-sm font-medium text-slate-700 mb-2">{label}</label>

      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm bg-white text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        {triggerLabel}
        <svg
          className={`w-4 h-4 text-slate-400 transition-transform shrink-0 ${open ? "rotate-180" : ""}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">

          {selected.length > 0 && (
            <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
              <span className="text-xs text-slate-400">{selected.length} selected</span>
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-xs text-red-500 hover:text-red-700 font-semibold"
              >
                Clear all
              </button>
            </div>
          )}

          {options.length === 0 ? (
            <div className="px-4 py-3 text-sm text-slate-400">No options</div>
          ) : (
            options.map((opt) => {
              const val = typeof opt === "object" ? opt.value : opt;
              const label = typeof opt === "object" ? opt.label : opt;
              const checked = selected.includes(val);
              return (
                <label
                  key={val}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 cursor-pointer text-sm text-slate-700"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(val)}
                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>{label}</span>
                </label>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// ── TEXT INPUT ─────────────────────────────────────────
const TextInput = ({ label, name, value, onChange, placeholder }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-2">{label}</label>
    <input
      type="text"
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full border border-slate-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
    />
  </div>
);

// ── AUDIO MIME HELPER ────────────────────────────────── 
// const getAudioMime = (filename) => {
//   if (!filename) return "audio/mpeg";
//   const ext = filename.split(".").pop().toLowerCase();
//   const map = { mp3: "audio/mpeg", mp4: "audio/mp4", m4a: "audio/x-m4a", wav: "audio/wav", ogg: "audio/ogg", webm: "audio/webm", aac: "audio/aac", flac: "audio/flac", wma: "audio/x-ms-wma", amr: "audio/amr", aiff: "audio/aiff", opus: "audio/opus", vox: "audio/vox" };
//   return map[ext] || "audio/mpeg";
// };

// ── OPTIONS ────────────────────────────────────────────
const DISPOSITION_OPTIONS = [
  "Quality", "Quality Low AQ", "Quality OO",
  "Validate", "Validate Low AQ", "Validate OO",
  "Callback", "Hold", "Sold", "Reject",
];

const VALIDATION_TYPE_OPTIONS = [
  { value: "elec", label: "Electricity Only" },
  { value: "gas", label: "Gas Only" },
  { value: "both", label: "Electricity + Gas" },
];

// ── MAIN ───────────────────────────────────────────────
const AdvancedSearch = () => {
  const today = new Date().toISOString().split("T")[0];
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user"));
  const isAdmin = user?.role === "admin";
  const isQuality = user?.role === "quality";

  // ── TEXT FILTERS ───────────────────────────────────────
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: today,
    leadId: "",
    businessPhone: "",
    businessName: "",
    mpanCore: "",
    mprnNo: "",
    contractEndFrom: "",
    contractEndTo: "",
    msnNo: "",
  });

  // ── MULTI-SELECT FILTERS ───────────────────────────────
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [selectedDispositions, setSelectedDispositions] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);

  const [agents, setAgents] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (isAdmin || isQuality) {
      axiosInstance.get("/users/agents")
        .then((res) => setAgents(res.data.data || []))
        .catch((err) => console.error(err));
    }
  },[isAdmin, isQuality]);

  const handleChange = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/leads/advanced-search", {
        params: {
          ...filters,
          agentName: selectedAgents.join(","),
          status: selectedDispositions.join(","),
          supplier: selectedSuppliers.join(","),
          validationType: selectedTypes.join(","),
        },
      });
      setResults(response.data.data || []);
      setSearched(true);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFilters({
      fromDate: "", toDate: today, leadId: "",
      businessPhone: "", businessName: "", mpanCore: "", mprnNo: "",
      contractEndFrom: "", contractEndTo: "", msnNo: "",
    });
    setSelectedAgents([]);
    setSelectedDispositions([]);
    setSelectedSuppliers([]);
    setSelectedTypes([]);
    setResults([]);
    setSearched(false);
  };

  // ── BADGES ─────────────────────────────────────────────
  const badgeColor = (d) => {
    if (d?.startsWith("Quality")) return "bg-green-100 text-green-700";
    if (d?.startsWith("Validate")) return "bg-blue-100 text-blue-700";
    if (d === "Callback") return "bg-yellow-100 text-yellow-700";
    if (d === "Reject") return "bg-red-100 text-red-700";
    if (d === "Hold") return "bg-gray-100 text-gray-600";
    return "bg-slate-100 text-slate-600";
  };

  const validationTypeBadge = (type) => {
    switch (type) {
      case "elec": return "bg-yellow-100 text-yellow-700";
      case "gas": return "bg-blue-100 text-blue-700";
      case "both": return "bg-purple-100 text-purple-700";
      default: return "bg-slate-100 text-slate-600";
    }
  };

  const tableHeaders = [
    "Lead ID", "Business", "Contact", "Phone",
    "Type", "Elec Supplier", "MPAN", "Elec End",
    "Gas Supplier", "MPRN", "Gas End", "MSN",
    ...(isAdmin || isQuality ? ["Agent"] : []),
    "Disposition", "Callback",  "Date",
  ];

  return (
    <div className="min-h-screen bg-[#dbe9f5] p-6">

      {/* ── SEARCH CARD ─────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
        <h2 className="text-2xl font-bold text-slate-800 mb-6">Advanced Search</h2>

        {/* ROW 1 — Dates / Disposition / Supplier */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">From Date</label>
            <input type="date" name="fromDate" value={filters.fromDate} onChange={handleChange}
              className="w-full border border-slate-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">To Date</label>
            <input type="date" name="toDate" value={filters.toDate} onChange={handleChange}
              className="w-full border border-slate-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>

          {/* DISPOSITION — multi-select */}
          <MultiSelect
            label="Lead Disposition"
            options={DISPOSITION_OPTIONS}
            selected={selectedDispositions}
            onChange={setSelectedDispositions}
            placeholder="All Dispositions"
          />

          {/* SUPPLIER — multi-select */}
          <MultiSelect
            label="Supplier (Elec or Gas)"
            options={supplierOptions}
            selected={selectedSuppliers}
            onChange={setSelectedSuppliers}
            placeholder="All Suppliers"
          />

        </div>

        {/* ROW 2 — Type / Agent / Lead ID / Phone */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">

          {/* VALIDATION TYPE — multi-select */}
          <MultiSelect
            label="Validation Type"
            options={VALIDATION_TYPE_OPTIONS}
            selected={selectedTypes}
            onChange={setSelectedTypes}
            placeholder="All Types"
          />

          {/* AGENT — multi-select, admin/quality only */}
          {(isAdmin || isQuality) && (
            <MultiSelect
              label="Agent Name"
              options={agents.map((a) => ({ value: a.first_name, label: a.first_name }))}
              selected={selectedAgents}
              onChange={setSelectedAgents}
              placeholder="All Agents"
            />
          )}

          <TextInput label="Lead ID" name="leadId" value={filters.leadId} onChange={handleChange} placeholder="e.g. 1042" />
          <TextInput label="Business Phone" name="businessPhone" value={filters.businessPhone} onChange={handleChange} placeholder="e.g. 07700..." />

 

        </div>

        {/* ROW 3 — MPAN / MPRN / Contract End */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
           <TextInput label="Business Name" name="businessName" value={filters.businessName} onChange={handleChange} placeholder="e.g. Acme Ltd" />
          <TextInput label="MPAN Core" name="mpanCore" value={filters.mpanCore} onChange={handleChange} placeholder="Electricity MPAN..." />
          <TextInput label="MPRN No" name="mprnNo" value={filters.mprnNo} onChange={handleChange} placeholder="Gas MPRN..." />

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Contract End From</label>
            <input type="date" name="contractEndFrom" value={filters.contractEndFrom} onChange={handleChange}
              className="w-full border border-slate-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Contract End To</label>
            <input type="date" name="contractEndTo" value={filters.contractEndTo} onChange={handleChange}
              className="w-full border border-slate-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
           <TextInput label="MSN No" name="msnNo" value={filters.msnNo} onChange={handleChange} placeholder="Meter serial no..." />
        </div>

        {/* ROW 4 — MSN */}
        {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <TextInput label="MSN No" name="msnNo" value={filters.msnNo} onChange={handleChange} placeholder="Meter serial no..." />
        </div> */}

        {/* BUTTONS */}
        <div className="flex gap-4 mt-6">
          <button onClick={handleSearch} disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-xl disabled:opacity-60">
            {loading ? "Searching..." : "Search"}
          </button>
          <button onClick={handleReset}
            className="bg-slate-200 hover:bg-slate-300 text-slate-800 font-semibold px-8 py-3 rounded-xl">
            Reset
          </button>
        </div>
      </div>

      {/* ── RESULTS ─────────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="p-6 border-b">
          <h3 className="text-xl font-bold text-slate-800">Search Results</h3>
          <p className="text-sm text-slate-500 mt-1">
            Total: <span className="font-semibold text-slate-700">{results.length}</span> records
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                {tableHeaders.map((h) => (
                  <th key={h} className="px-4 py-4 text-left text-xs font-semibold text-slate-500 uppercase whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {results.length > 0 ? results.map((item) => (
                <tr key={item.id} className="border-b hover:bg-slate-50 transition">

                  <td className="px-4 py-4 font-mono text-xs">
                    <button onClick={() => navigate(`/leads/advanced-search/${item.id}`)}
                      className="text-blue-600 hover:text-blue-800 font-bold hover:underline">
                      #{item.id}
                    </button>
                  </td>

                  <td className="px-4 py-4">
                    <div className="font-semibold text-slate-800">{item.business_name}</div>
                    <div className="text-xs text-slate-500">{item.business_address}</div>
                  </td>

                  <td className="px-4 py-4 text-slate-700">{item.contact_name}</td>
                  <td className="px-4 py-4 text-slate-600 text-xs whitespace-nowrap">{item.business_phone || "—"}</td>

                  <td className="px-4 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold uppercase ${validationTypeBadge(item.validation_type)}`}>
                      {item.validation_type === "elec" ? "Electricity" : item.validation_type === "gas" ? "Gas" : item.validation_type === "both" ? "Elec + Gas" : "—"}
                    </span>
                  </td>

                  <td className="px-4 py-4 text-slate-700 whitespace-nowrap">{item.electricity_supplier || <span className="text-slate-400">—</span>}</td>
                  <td className="px-4 py-4 font-mono text-xs text-slate-600">{item.mpan_core || <span className="text-slate-400">—</span>}</td>
                  <td className="px-4 py-4 text-slate-500 text-xs whitespace-nowrap">{item.electricity_contract_end ? new Date(item.electricity_contract_end).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-4 text-slate-700 whitespace-nowrap">{item.gas_supplier || <span className="text-slate-400">—</span>}</td>
                  <td className="px-4 py-4 font-mono text-xs text-slate-600">{item.mprn_no || <span className="text-slate-400">—</span>}</td>
                  <td className="px-4 py-4 text-slate-500 text-xs whitespace-nowrap">{item.gas_contract_end ? new Date(item.gas_contract_end).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-4 font-mono text-xs text-slate-600">{item.electricity_msn || item.gas_msn || <span className="text-slate-400">—</span>}</td>

                  {(isAdmin || isQuality) && (
                    <td className="px-4 py-4">
                      {item.created_by_name
                        ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-medium">👤 {item.created_by_name}</span>
                        : <span className="text-slate-400">—</span>}
                    </td>
                  )}

                  <td className="px-4 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${badgeColor(item.lead_disposition)}`}>
                      {item.lead_disposition || "—"}
                    </span>
                  </td>

                  <td className="px-4 py-4 text-slate-500 text-xs whitespace-nowrap">{item.callback_date ? new Date(item.callback_date).toLocaleString() : "—"}</td>

                  {/* <td className="px-4 py-4">
                    {item.vox_file
                      ? <audio controls preload="metadata" className="w-44"><source src={`https://ratcrm.zentelex.com/uploads/${item.vox_file}`} type={getAudioMime(item.vox_file)} /></audio>
                      : <span className="text-slate-400 text-xs">No File</span>}
                  </td> */}

                  <td className="px-4 py-4 text-slate-500 text-xs whitespace-nowrap">{new Date(item.created_at).toLocaleDateString()}</td>

                </tr>
              )) : (
                <tr>
                  <td colSpan={tableHeaders.length} className="text-center py-16 text-slate-400">
                    {searched ? "No results found for your filters" : "Use filters above and click Search"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdvancedSearch;